/** @jsxImportSource @emotion/react */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from 'react'
import req from '@sportninja/common/api/request'

// Types
export type TaxData = {
  id?: string
  name?: string
  description?: string
  tax_rate?: number | string
  currency_country_id?: number | string
}

type TaxCustomError = {
  taxName: string | null
  taxDescription: string | null
  taxRate: string | null
  currencyCountryId: string | null
  message: string | null
}

type TaxState = {
  id: string | null
  taxName: string
  taxDescription: string
  taxRate: string
  currencyCountryId: string
  isLoading: boolean
  error: string | null
  fieldErrors: string[]
  customError: TaxCustomError
}

type TaxAction =
  | { type: 'SET_FIELD'; field: keyof TaxState; value: unknown }
  | { type: 'SET_LOADING'; value: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_FIELD_ERRORS'; errors: string[] }
  | { type: 'SET_CUSTOM_ERROR_BY_FIELD'; field: string; message: string | null }
  | { type: 'CLEAR_CUSTOM_ERROR_BY_FIELD'; field: string }
  | { type: 'CLEAR_ALL_ERRORS' }
  | { type: 'RESET_FORM' }
  | { type: 'INIT_EDIT_MODE'; data: TaxData }
  | { type: 'SET_DEFAULT_COUNTRY'; countryId: string }

const initialCustomError: TaxCustomError = {
  taxName: null,
  taxDescription: null,
  taxRate: null,
  currencyCountryId: null,
  message: null,
}

const initialState: TaxState = {
  id: null,
  taxName: '',
  taxDescription: '',
  taxRate: '',
  currencyCountryId: '',
  isLoading: false,
  error: null,
  fieldErrors: [],
  customError: initialCustomError,
}

function taxReducer(state: TaxState, action: TaxAction): TaxState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }

    case 'SET_LOADING':
      return { ...state, isLoading: action.value }

    case 'SET_ERROR':
      return { ...state, error: action.error }

    case 'SET_FIELD_ERRORS':
      return { ...state, fieldErrors: action.errors }

    case 'SET_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: action.message,
        },
        fieldErrors: action.message
          ? [...new Set([...state.fieldErrors, action.field])]
          : state.fieldErrors,
      }

    case 'CLEAR_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: null,
        },
        fieldErrors: state.fieldErrors.filter((e) => e !== action.field),
      }

    case 'CLEAR_ALL_ERRORS':
      return {
        ...state,
        error: null,
        fieldErrors: [],
        customError: initialCustomError,
      }

    case 'RESET_FORM':
      return { ...initialState }

    case 'INIT_EDIT_MODE':
      return {
        ...state,
        id: action.data.id || null,
        taxName: action.data.name || '',
        taxDescription: action.data.description || '',
        taxRate: String(action.data.tax_rate ?? ''),
        currencyCountryId: String(action.data.currency_country_id ?? ''),
      }

    case 'SET_DEFAULT_COUNTRY':
      return {
        ...state,
        currencyCountryId: action.countryId,
      }

    default:
      return state
  }
}

// Context type
type TaxContextValue = {
  state: TaxState
  dispatch: React.Dispatch<TaxAction>
  isEdit: boolean
  onSubmit: () => Promise<void>
  onCancel: () => void
  setCustomErrorByField: (field: string, message: string | null) => void
  clearCustomErrorByField: (field: string) => void
}

const TaxContext = createContext<TaxContextValue | null>(null)

export const useTaxContext = () => {
  const context = useContext(TaxContext)
  if (!context) {
    throw new Error('useTaxContext must be used within TaxProvider')
  }
  return context
}

// Provider props
type TaxProviderProps = {
  children: ReactNode
  isOpen: boolean
  onClose: () => void
  initialData?: TaxData
  accountUid: string
  onSuccess: () => void
  defaultCountryId?: string
}

export const TaxProvider: React.FC<TaxProviderProps> = ({
  children,
  onClose,
  initialData,
  accountUid,
  onSuccess,
  defaultCountryId,
}) => {
  const [state, dispatch] = useReducer(taxReducer, initialState)
  const isEdit = Boolean(initialData?.id)

  const setCustomErrorByField = useCallback(
    (field: string, errorMessage: string | null) => {
      dispatch({
        type: 'SET_CUSTOM_ERROR_BY_FIELD',
        field,
        message: errorMessage,
      })
    },
    []
  )

  const clearCustomErrorByField = useCallback((field: string) => {
    dispatch({ type: 'CLEAR_CUSTOM_ERROR_BY_FIELD', field })
  }, [])

  const setCustomErrors = useCallback(
    (invalid_fields: Record<string, string | string[]>) => {
      const normalizeError = (error: string | string[]): string => {
        return Array.isArray(error) ? error.join(', ') : error
      }

      Object.entries(invalid_fields).forEach(([field, error]) => {
        setCustomErrorByField(field, normalizeError(error))
      })
    },
    [setCustomErrorByField]
  )

  // Initialize edit mode or set default country
  React.useEffect(() => {
    if (initialData && isEdit) {
      dispatch({ type: 'INIT_EDIT_MODE', data: initialData })
    } else if (defaultCountryId) {
      dispatch({ type: 'SET_DEFAULT_COUNTRY', countryId: defaultCountryId })
    }
  }, [initialData, isEdit, defaultCountryId])

  const onSubmit = useCallback(async () => {
    dispatch({ type: 'CLEAR_ALL_ERRORS' })
    dispatch({ type: 'SET_LOADING', value: true })
    try {
      const payload = {
        name: state.taxName.trim(),
        description: state.taxDescription.trim(),
        tax_rate: state.taxRate,
        currency_country_id: Number(state.currencyCountryId),
      }

      if (isEdit && state.id) {
        await req(`/account/${accountUid}/tax/${state.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
      } else {
        await req(`/account/${accountUid}/tax`, {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      }

      onSuccess()
      onClose()
    } catch (error: unknown) {
      const err = error as {
        invalid_fields?: Record<string, string | string[]>
        message?: string
      }

      if (err.invalid_fields) {
        setCustomErrors(err.invalid_fields)
      } else {
        const errorMessage =
          err.message ||
          (error instanceof Error ? error.message : 'An error occurred')
        dispatch({ type: 'SET_ERROR', error: errorMessage })
      }
    } finally {
      dispatch({ type: 'SET_LOADING', value: false })
    }
  }, [state, isEdit, accountUid, onSuccess, onClose, setCustomErrors])

  const onCancel = useCallback(() => {
    dispatch({ type: 'RESET_FORM' })
    onClose()
  }, [onClose])

  const value: TaxContextValue = {
    state,
    dispatch,
    isEdit,
    onSubmit,
    onCancel,
    setCustomErrorByField,
    clearCustomErrorByField,
  }

  return <TaxContext.Provider value={value}>{children}</TaxContext.Provider>
}

export default TaxContext
